import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  useV2CreateProjectContactMutation,
  useV2UpdateProjectContactMutation,
  useV2CreateContactMutation,
  useV2CreateContactForClientMutation,
  useV2ListContactsForAccountQuery,
  useV2ListContactsForClientQuery,
} from "@generated";
import { handleAlert, formatDate } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import AutoCompleteText from "@components/Forms/AutoCompleteText/AutoCompleteText";
import FormTextField from "@components/Forms/FormTextField";
import FormEmailField from "@components/Forms/FormEmailField";
import FormPhoneField from "@components/Forms/FormPhoneField";
import ControlledDropDown from "@components/Forms/ControlledDropDown";
import ClientRecordPicker, {
  PickedRecord,
} from "@components/ClientRecordPicker/ClientRecordPicker";
import ProjectVariables from "../../ProjectVariables/ProjectVariables";
import {
  Contact,
  ContactType,
  CONTACT_TYPE_LABELS,
  ProjectVariable,
} from "../types/Contact";

interface Props {
  projectId: string;
  contact?: Contact;
  contacts: Contact[];
  clientId: string | null;
  projectVariableDefinitions: ProjectVariable[];
  formId?: string;
}

const ContactForm: React.FC<Props> = ({
  projectId,
  contact,
  contacts,
  clientId,
  projectVariableDefinitions,
  formId,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const navigate = useNavigate();

  const isEdit = !!contact;
  const today = new Date();

  const [name, setName] = useState(contact?.name || "");
  const [title, setTitle] = useState(contact?.title || "");
  const [email, setEmail] = useState(contact?.email || "");
  const [phone, setPhone] = useState(contact?.phone || "");
  const [contactType, setContactType] = useState<ContactType>(
    contact?.contactType || "primary_customer_contact"
  );
  const [userTypedName, setUserTypedName] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null
  );
  const [nameSearchTerm, setNameSearchTerm] = useState(contact?.name || "");
  // ConnectWise contact picked on create; the API adopts it as a client
  // contact, so we must not also create one here.
  const [crmContactId, setCrmContactId] = useState<string | null>(null);

  const [requiredProjectVariables, setRequiredProjectVariables] = useState<
    ProjectVariable[]
  >([]);
  const [unrequiredProjectVariables, setUnrequiredProjectVariables] = useState<
    ProjectVariable[]
  >([]);

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const [createProjectContact] = useV2CreateProjectContactMutation();
  const [updateProjectContact] = useV2UpdateProjectContactMutation();
  const [createAccountContact] = useV2CreateContactMutation();
  const [createClientContact] = useV2CreateContactForClientMutation();

  const isInternalType =
    contactType === "internal_contact" ||
    contactType === "primary_internal_contact";

  const { data: accountContactsData } = useV2ListContactsForAccountQuery(
    {
      filter: { name: nameSearchTerm },
      page: { size: 500 },
    },
    { skip: !isInternalType || nameSearchTerm.length === 0 }
  );

  const { data: clientContactsData } = useV2ListContactsForClientQuery(
    {
      clientId: clientId as string,
      filter: { name: nameSearchTerm },
      page: { size: 500 },
    },
    {
      skip: isInternalType || !clientId || nameSearchTerm.length === 0,
    }
  );

  const contactSuggestions = isInternalType
    ? accountContactsData?.data || []
    : clientContactsData?.data || [];

  useEffect(() => {
    if (isEdit && contact) {
      setName(contact.name);
      setTitle(contact.title);
      setEmail(contact.email);
      setPhone(contact.phone);
      setContactType(contact.contactType);

      const pvs = contact.projectVariables || [];
      setRequiredProjectVariables(pvs.filter((v) => v.required === true));
      setUnrequiredProjectVariables(pvs.filter((v) => v.required === false));
    } else if (!isEdit && projectVariableDefinitions.length > 0) {
      const reqVars = projectVariableDefinitions
        .filter((v) => v.required)
        .map((v) => ({
          ...v,
          value: v.variable_type === "date" ? formatDate(today) : "",
        }));
      const unreqVars = projectVariableDefinitions
        .filter((v) => !v.required)
        .map((v) => ({ ...v, value: "" }));
      setRequiredProjectVariables(reqVars);
      setUnrequiredProjectVariables(unreqVars);
    }
  }, [contact, projectVariableDefinitions]);

  const validateVariables = (allVars: ProjectVariable[]): string | null => {
    for (const variable of allVars) {
      const varType = variable.variable_type;
      if (
        varType === "text" &&
        (!variable.select_options || variable.select_options.length === 0)
      ) {
        const val = variable.value || "";
        if (variable.minimum != null && val.length < variable.minimum) {
          return `Required text is too short (minimum is ${variable.minimum} characters)`;
        }
        if (variable.maximum != null && val.length > variable.maximum) {
          return `Required text is too long (maximum is ${variable.maximum} characters)`;
        }
      }
    }
    return null;
  };

  const findOrCreateContact = async (): Promise<string | null> => {
    if (selectedContactId) {
      return selectedContactId;
    }

    if (crmContactId) {
      return null;
    }

    if (!userTypedName || !name.trim()) {
      return null;
    }

    const newContactPayload = {
      data: {
        type: "contacts" as const,
        attributes: { name, email, phone, title },
      },
    };

    try {
      if (isInternalType) {
        const result = await createAccountContact({
          body: newContactPayload as any,
        }).unwrap();
        return String(result?.data?.id);
      } else if (clientId) {
        const result = await createClientContact({
          clientId,
          body: newContactPayload as any,
        }).unwrap();
        return String(result?.data?.id);
      }
    } catch {
      // Contact creation is best-effort; proceed with project contact
    }

    return null;
  };

  const saveContact = async () => {
    if (!name.trim()) {
      setErrorMessages("Contact name can't be blank");
      setShowFailAlert(true);
      return;
    }

    const allVars = requiredProjectVariables.concat(unrequiredProjectVariables);
    const varError = validateVariables(allVars);
    if (varError) {
      setErrorMessages(varError);
      setShowFailAlert(true);
      return;
    }

    const contactId = await findOrCreateContact();

    const projectVariablesPayload = allVars.map((v) => ({
      name: v.name,
      value: v.value,
    }));

    const attributes: any = {
      name,
      email,
      phone,
      title,
      "contact-type": contactType,
      "project-variables": projectVariablesPayload,
    };

    if (!isEdit && !isInternalType && crmContactId) {
      attributes["crm-contact-id"] = crmContactId;
    }

    const relationships: any = {
      project: { data: { type: "projects", id: projectId } },
    };

    if (contactId) {
      relationships.contact = { data: { type: "contacts", id: contactId } };
    }

    const payload = {
      data: {
        type: "project-contacts" as const,
        ...(isEdit ? { id: contact!.id } : {}),
        attributes,
        relationships,
      },
    };

    try {
      if (isEdit) {
        await updateProjectContact({
          id: contact!.id,
          body: payload as any,
        }).unwrap();
        navigate(`/projects/${projectId}/project_contacts`, {
          state: { edit: true },
        });
      } else {
        await createProjectContact({
          body: payload as any,
        }).unwrap();
        navigate(`/projects/${projectId}/project_contacts`, {
          state: { new: true },
        });
      }
    } catch (err: any) {
      const message =
        err?.data?.errors?.[0]?.detail ||
        "Something went wrong. Your changes could not be saved.";
      setErrorMessages(message);
      setShowFailAlert(true);
    }
  };

  const contactTypeOptions = (
    Object.entries(CONTACT_TYPE_LABELS) as [ContactType, string][]
  ).map(([value, label]) => (
    <option key={value} value={value}>
      {label}
    </option>
  ));

  const applyPick = (rec: PickedRecord) => {
    setName(rec.fields.name ?? "");
    setTitle(rec.fields.title ?? "");
    setEmail(rec.fields.email ?? "");
    setPhone(rec.fields.phone ?? "");
    if (rec.source === "client") {
      setSelectedContactId(rec.id);
      setCrmContactId(null);
    } else {
      setSelectedContactId(null);
      setCrmContactId(rec.crmId);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setNameSearchTerm(value);
    if (!value) setCrmContactId(null);

    const match = (contactSuggestions as any[]).find(
      (c: any) => c.attributes?.name === value
    );
    if (match) {
      // The API takes crm-contact-id or a contact relationship, not both.
      setCrmContactId(null);
      setSelectedContactId(String(match.id));
      setEmail(match.attributes?.email || "");
      setTitle(match.attributes?.title || "");
      setPhone(match.attributes?.phone || "");
    } else {
      setSelectedContactId(null);
    }
  };

  return (
    <>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          saveContact();
        }}
      >
        <Row>
          <Col xs={6}></Col>
          <Col xs={6}>
            <ControlledDropDown
              label="Contact Type"
              value={contactType}
              options={contactTypeOptions}
              onChange={(e: any) => {
                const nextType = e.target.value as ContactType;
                setContactType(nextType);
                setSelectedContactId(null);
                if (
                  nextType === "internal_contact" ||
                  nextType === "primary_internal_contact"
                ) {
                  setCrmContactId(null);
                }
                setNameSearchTerm(name);
              }}
              defaultValue={undefined}
              required={false}
              readOnly={false}
              disabled={false}
              fieldFooter={undefined}
            />
          </Col>
        </Row>

        {!isEdit && !isInternalType && (
          <Row>
            <Col xs={12}>
              <ClientRecordPicker
                kind="contact"
                projectId={projectId}
                clientId={clientId}
                onPick={applyPick}
              />
            </Col>
          </Row>
        )}

        <Row>
          <Col xs={6}>
            <AutoCompleteText
              label="Name *"
              placeholder="John Smith"
              value={name}
              required={true}
              suggestionList={(contactSuggestions as any[]).map(
                (c: any) => c.attributes?.name || ""
              )}
              monitorValue={handleNameChange}
              setTextChanged={(bool: boolean) => {
                setUserTypedName(bool);
              }}
              id={undefined}
              crmId={undefined}
            />
          </Col>
          <Col xs={6}>
            <FormTextField
              label="Title"
              value={title}
              onChange={(e: any) => setTitle(e.target.value)}
              id={undefined}
              placeholder={undefined}
              required={false}
              readOnly={false}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={6}>
            <FormEmailField
              label="Email"
              placeholder="john.smith@customer.com"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              id={undefined}
              required={false}
            />
          </Col>
          <Col xs={6}>
            <FormPhoneField
              label="Phone"
              placeholder="2125553456"
              value={phone}
              onChange={(e: any) => setPhone(e.target.value)}
              id={undefined}
              required={false}
            />
          </Col>
        </Row>

        <Row>
          <ProjectVariables
            arr={requiredProjectVariables}
            account_slug={accountSlug}
            project_id={projectId}
            setState={setRequiredProjectVariables}
            setVariableChanged={null}
          />
          <ProjectVariables
            arr={unrequiredProjectVariables}
            account_slug={accountSlug}
            project_id={projectId}
            setState={setUnrequiredProjectVariables}
            setVariableChanged={null}
          />
        </Row>
      </Form>
    </>
  );
};

export default ContactForm;
